import * as React from 'react';
import { ROW_RUNGS } from "../../lib/row-rungs";

import { cn } from '../../lib/utils';
import { Box, type BoxProps } from '../../components/ui/box';
import { Button } from '../../components/ui/button';
import {
  Icon,
  type IconName,
  type IconSource,
} from '../../components/ui/icon';
import { Text, type TextProps } from '../../components/ui/text';
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { useDataTableDensity } from './data-table-context';
import type { SpacingToken } from '../../components/ui/box-classes';
import type { DataTableDensity } from './data-table-types';
import type { Omittable } from "../../lib/props";

// Density → Text size prop (mirrors DENSITY_CONFIG.textSize; the header text
// takes the size through Text's own variant so the responsive default pair
// never fights it).
const DENSITY_TEXT_SIZE: Record<DataTableDensity, TextProps['size']> = {
  compact: 'xs',
  regular: 'sm',
  comfortable: 'sm',
  spacious: 'base',
};

export type DataTableGroupHeaderProps = Omit<BoxProps, 'title'> &
  Omittable<{
    title: React.ReactNode;
    /** The group's name as text ("Todo"), for the caret's accessible name
     *  when the title is a node. Each group's caret then names its group. */
    groupLabel?: string;
    count?: number | string;
    icon?: IconSource;
    actions?: React.ReactNode;
    showChevron?: boolean;
    /** Inert group: an empty group with nothing to reveal (no add-row
     *  footer). Expand/collapse no-ops and the chevron stays visible as a
     *  collapsed-pointing reference (`>`, faded) — there is nothing to open,
     *  but the band should still read as a group. */
    disabled?: boolean;
    /** Controlled expanded state. */
    isExpanded?: boolean;
    /** Toggle callback. */
    onToggle?: () => void;
    /**
     * Leading alignment (select-column tables): fixed width of the box the
     * chevron renders in, sized by DataTable so the title lands on the first
     * consumer column's text line. Omit for natural flow (chevron + gap).
     */
    leadingWidth?: number;
    /** Left margin positioning the chevron inside the leading box (centers it
     *  over the select column's checkboxes). Only used with `leadingWidth`. */
    chevronMargin?: number;
    /**
     * Hover-reveal the actions cluster: hidden until the pointer is anywhere
     * over the group's `tgroup` scope (DataTable wraps each group — header
     * and rows — in one). Reveal is CSS (`hoverReveal` + `revealOnTouch`: the
     * band's actions stand on a coarse pointer — a band is one line per group,
     * not a column of chrome, and its actions are the group's own controls),
     * so touch keeps the actions visible.
     */
    actionsOnGroupHover?: boolean;
    /**
     * The title node owns its own interaction — a press-to-rename trigger, an
     * in-place editor. Interactive content cannot nest inside the title's
     * toggle `<button>`, so this seats the title in a plain box beside the
     * chevron instead: pressing it fires the title's own handlers, and only
     * the chevron still toggles the group.
     */
    interactiveTitle?: boolean;
  }>;

type DataTableGroupChevronProps = Omittable<{
  icon: IconName;
  /** Inert (empty) group: the caret is a faded, non-interactive reference. */
  disabled?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  groupLabel?: string | undefined;
}>;

/**
 * The group's toggle caret. On a live group it is its own ghost icon button —
 * a hover pill with a "Collapse/Expand group" tooltip (the reference idiom) —
 * sitting beside the title button, which also toggles. An inert group keeps
 * the bare faded caret: there is nothing to open, so no affordance.
 */
function DataTableGroupChevron({
  icon,
  disabled,
  isExpanded,
  onToggle,
  groupLabel,
}: DataTableGroupChevronProps) {
  if (disabled) {
    // 40% muted ink: the `subtle` rung (72%) reads too close to the live
    // caret's full muted — an inert reference must be legibly quieter than
    // a control.
    return (
      <Icon
        name={icon}
        size="md"
        className="shrink-0 text-muted-foreground/40"
      />
    );
  }
  const caret = (
    <Icon
      name={icon}
      size="md"
      color="muted"
      // opacity-100 opts out of Button's 80% dimming of bare svgs — the caret
      // is the group's primary glyph and keeps full muted ink in every state.
      className="shrink-0 opacity-100"
    />
  );
  const label = isExpanded ? 'Collapse group' : 'Expand group';
  // The tooltip keeps the short verb; the name says which group, so each
  // caret on the page differs.
  const name = groupLabel
    ? `${isExpanded ? 'Collapse' : 'Expand'} ${groupLabel}`
    : label;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={name}
            aria-expanded={!!isExpanded}
            onClick={onToggle}
            // The pill paints 28px (24px at sm+) but the negative margin keeps
            // its layout footprint at the caret's own 16px, so the leading
            // alignment (caret centered over the select column) and the 8px
            // gap to the title both hold.
            className="-m-1.5 sm:-m-1"
          />
        }
      >
        {caret}
      </TooltipTrigger>
      <TooltipPopup side="top">{label}</TooltipPopup>
    </Tooltip>
  );
}

type DataTableGroupHeaderBodyProps = Omittable<{
  control: React.ReactNode;
  count?: number | string;
  actions?: React.ReactNode;
  actionsOnGroupHover?: boolean;
}>;

function DataTableGroupHeaderBody({
  control,
  count,
  actions,
  actionsOnGroupHover,
}: DataTableGroupHeaderBodyProps) {
  return (
    <>
      <Box minWidth={0} maxWidth="100%"
        direction="row"
        align="center"
        className="shrink"
      >
        {control}
        {count !== undefined ? (
          // Fixed 12px/medium regardless of density — the count is secondary
          // chrome and shouldn't scale with the row text. 12px leading pad:
          // one optical rhythm with everything to its right — the outer
          // gap-3 (12px) to the actions cluster and the 12px glyph-to-glyph
          // spacing between the action icons themselves.
          <Text
            variant="muted"
            weight="medium"
            truncate
            size="xs"
            className="shrink-0 pl-3"
          >
            {typeof count === "number" ? count.toLocaleString() : count}
          </Text>
        ) : null}
      </Box>
      {actions ? (
        <Box
          direction="row"
          align="center"
          gap="xs"
          shrink={false}
          hoverReveal={actionsOnGroupHover ? "tgroup" : undefined}
          revealOnTouch={actionsOnGroupHover || undefined}
          // -ml-1 cancels the ghost buttons' ~4px internal glyph inset so
          // the VISUAL gap from the count to the first action icon equals
          // the outer gap-3 — the same 12px rhythm as count and the icons.
          className="-ml-1"
        >
          {actions}
        </Box>
      ) : null}
    </>
  );
}

/**
 * Sticky group header — chevron toggle, title, count and
 * actions. Expanded state is fully controlled (`isExpanded` + `onToggle`);
 * the core owns group open/close state.
 */
export function DataTableGroupHeader({
  title,
  groupLabel,
  count,
  icon,
  actions,
  actionsOnGroupHover = false,
  showChevron = true,
  disabled = false,
  isExpanded,
  onToggle,
  leadingWidth,
  chevronMargin,
  interactiveTitle = false,
  className,
  ...props
}: DataTableGroupHeaderProps) {
  const density = useDataTableDensity();
  const densityTextSize = DENSITY_TEXT_SIZE[density];
  // An inert (empty) group reads as collapsed regardless of stored state:
  // there is nothing to expand, so a down caret would promise content.
  // The stable caret keys match the sidebar sections: expanded points down,
  // and collapsed points right.
  const chevronIcon: IconName =
    isExpanded && !disabled ? "caret-down" : "caret-right";

  // Leading-aligned mode: the chevron sits at a fixed offset inside a
  // fixed-width box (DataTable's select-column alignment — see its
  // groupLeading derivation), so the title after it lands on the rows'
  // first-column text line.
  const wrapChevron = (chevron: React.ReactNode) =>
    leadingWidth !== undefined ? (
      <Box
        direction="row"
        align="center"
        shrink={false}
        width={leadingWidth}
      >
        {/* Zero margin renders a zero-size spacer — no branch to keep. */}
        <Box width={chevronMargin ?? 0} />
        {chevron}
      </Box>
    ) : (
      chevron
    );

  return (
    <Box
      direction="row"
      align="center"
      // Fixed band at every density (ROW_RUNGS.tableGroupBand, 36): a step
      // under the rows and column header, so a group's title reads as a
      // divider between lists, not as another row — content centers inside
      // it instead of padding the row taller.
      height={ROW_RUNGS.tableGroupBand}
      className={cn('gap-3', className)}
      {...props}
    >
      <DataTableGroupHeaderBody
        count={count}
        actions={actions}
        actionsOnGroupHover={actionsOnGroupHover}
        control={
          // The caret button and the title button are SIBLINGS — the caret
          // cannot nest inside the title's button element, and both toggle,
          // so the whole label area stays one click target in practice.
          // With `interactiveTitle`, only the caret toggles.
          <Box minWidth={0}
            direction="row"
            align="center"
            gap="sm"
            className="shrink self-stretch"
          >
            {showChevron &&
              wrapChevron(
                <DataTableGroupChevron
                  icon={chevronIcon}
                  disabled={disabled}
                  isExpanded={isExpanded}
                  onToggle={onToggle}
                  groupLabel={groupLabel}
                />,
              )}
            <Box minWidth={0}
              // An interactive title owns its own presses (rename trigger,
              // in-place editor) — interactive content cannot nest inside a
              // `<button>`, so the toggle retreats to the chevron and the
              // title renders in a plain box.
              as={interactiveTitle ? undefined : "button"}
              direction="row"
              align="center"
              gap="sm"
              onClick={disabled || interactiveTitle ? undefined : onToggle}
              // An inert group READS collapsed (forced right caret, empty
              // body) whatever its stored expand state — the a11y tree says
              // the same thing the caret does.
              aria-expanded={
                interactiveTitle ? undefined : !!isExpanded && !disabled
              }
              aria-disabled={
                (!interactiveTitle && disabled) || undefined
              }
              className={cn(
                'shrink self-stretch',
                disabled || interactiveTitle
                  ? 'cursor-default'
                  : 'cursor-pointer'
              )}
            >
              {icon ? (
                typeof icon === "string" ? (
                  <Icon name={icon} size="lg" color="muted" />
                ) : (
                  <Icon as={icon} size="lg" color="muted" />
                )
              ) : null}
              <Box minWidth={0} className="shrink">
                {React.isValidElement(title) ? (
                  title
                ) : (
                  <Text weight="medium" truncate size={densityTextSize}>
                    {title}
                  </Text>
                )}
              </Box>
            </Box>
          </Box>
        }
      />
    </Box>
  );
}

export type DataTableSectionHeaderProps = Omittable<{
  title: React.ReactNode;
  /** Summed group count; omitted while any group's total is still unknown. */
  count?: number;
  isExpanded: boolean;
  onToggle: () => void;
  /** The table's horizontal inset, so the band's chevron shares the groups' line. */
  paddingX?: SpacingToken;
}>;

/**
 * The band of a section — the tier above the groups. It reuses the group
 * band's anatomy (chevron, title, count) at a heavier weight and a taller
 * fixed height so it reads as the container of the stage-sized bands under
 * it, not as one more of them. A string title takes the section's own type
 * scale; an element title is seated as given.
 */
export function DataTableSectionHeader({
  title,
  count,
  isExpanded,
  onToggle,
  paddingX,
}: DataTableSectionHeaderProps) {
  return (
    <DataTableGroupHeader
      data-slot="table-section-band"
      height={40}
      paddingX={paddingX}
      title={
        React.isValidElement(title) ? (
          title
        ) : (
          <Text weight="semibold" truncate size="base">
            {title}
          </Text>
        )
      }
      count={count}
      isExpanded={isExpanded}
      onToggle={onToggle}
    />
  );
}
